import React, { createContext, useContext, ReactNode } from 'react';
import type { User } from '../config/api';

// ============================================================================
// REVAMP: the product is now fully PUBLIC — no login required to use any module.
//
// AuthContext is kept as a thin "guest" shim so existing components that import
// `useAuth()` still compile. It always reports an anonymous guest and exposes
// no-op auth functions. The real login/register/profile machinery has been
// retired (accounts may be reintroduced later). Components should stop relying
// on `isAuthenticated` to gate features.
// ============================================================================

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, otpCode: string) => Promise<void>;
  passwordLogin: (email: string, password: string) => Promise<void>;
  register: (email: string, firstName: string, lastName: string, password: string) => Promise<void>;
  requestOTP: (email: string) => Promise<void>;
  updatePassword: (newPassword: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// A permanent guest state.
const guestState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

const notEnabled = async (): Promise<void> => {
  // Accounts are disabled in the public revamp. Kept as a resolved no-op so any
  // lingering caller doesn't crash. Reimplement when login returns.
  return undefined;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const value: AuthContextType = {
    ...guestState,
    login: notEnabled,
    passwordLogin: notEnabled,
    register: notEnabled,
    requestOTP: notEnabled,
    updatePassword: notEnabled,
    logout: notEnabled,
    updateProfile: notEnabled,
    clearError: () => undefined,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
